import { getPlatform } from "@/lib/iap";
import React from "react";
import { useNavigate } from "react-router-dom";

const PLATFORM = getPlatform();
const HAS_IOS_NATIVE_AUTH =
  PLATFORM === "apple" && window.webkit?.messageHandlers["socialAuth"];

interface IOSSocialLoginProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  provider: any;
  isTemp: boolean;
  userID: number;
  children: React.ReactNode;
  providerType: "google" | "apple";
}

export const IOSSocialLogin = function ({
  onSuccess,
  onError,
  provider,
  isTemp,
  userID,
  children,
  providerType,
}: IOSSocialLoginProps) {
  const navigate = useNavigate();

  if (!provider || !HAS_IOS_NATIVE_AUTH) {
    return null;
  }

  const handleLogin = async () => {
    localStorage.setItem("provider", JSON.stringify(provider));
    localStorage.setItem("isTemp", JSON.stringify(isTemp));
    localStorage.setItem("parrotUserId", JSON.stringify(userID));
    try {
      const { token, email, cleanUp } = await socialSignIn(providerType);
      cleanUp();
      navigate(
        `/redirect?code=${token}&email=${email}&state=${provider.state}&native=true`
      );
      onSuccess?.();
    } catch (error) {
      onError?.(error as Error);
    }
  };

  return React.cloneElement(children as React.ReactElement, {
    onClick: handleLogin,
  });
};

interface SignInResult {
  token: string;
  email: string;
  cleanUp: () => void;
}

export async function socialSignIn(
  providerType: "google" | "apple"
): Promise<SignInResult> {
  if (!HAS_IOS_NATIVE_AUTH) {
    return { token: "", email: "", cleanUp: () => {} };
  }

  return new Promise((resolve, reject) => {
    const handleExternalSocialAuth = (
      event: CustomEvent<{
        accessToken: string;
        provider: "google" | "apple";
        email: string;
      }>
    ) => {
      const { accessToken, email: gotEmail } = event.detail;

      const cleanUp = () => {
        window.removeEventListener(
          "external-social-auth",
          handleExternalSocialAuth as EventListener
        );
      };
      resolve({ token: accessToken, email: gotEmail, cleanUp });
    };

    window.addEventListener(
      "external-social-auth",
      handleExternalSocialAuth as EventListener
    );

    if (HAS_IOS_NATIVE_AUTH) {
      try {
        window.webkit.messageHandlers.socialAuth.postMessage(providerType);
      } catch (error) {
        reject(error);
      }
    } else {
      reject(new Error("No native auth handler found"));
    }
  });
}
